// Modal de criar/editar contato de uma lista (/contact-lists/:id/contacts),
// no padrão Aeros Dark. As regras de validação são as mesmas do schema Yup
// anterior: nome 2–50 obrigatório, número 8–50 e e-mail válido quando
// preenchidos.
import React, { useState, useEffect, useContext, useRef } from "react";

import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import TextField from "@material-ui/core/TextField";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY = { name: "", number: "", email: "" };

const ContactListItemModal = ({
  open,
  onClose,
  contactId,
  initialValues,
  onSave,
}) => {
  const m = useAerosModalStyles();
  const isMounted = useRef(true);

  const {
    user: { companyId },
  } = useContext(AuthContext);
  const { contactListId } = useParams();

  const [contact, setContact] = useState(EMPTY);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    setTouched(false);

    const fetchContact = async () => {
      if (initialValues) {
        setContact((prevState) => ({ ...prevState, ...initialValues }));
      }

      if (!contactId) {
        if (!initialValues) setContact(EMPTY);
        return;
      }

      try {
        const { data } = await api.get(`/contact-list-items/${contactId}`);
        if (isMounted.current) {
          setContact({
            name: data?.name || "",
            number: data?.number || "",
            email: data?.email || "",
          });
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchContact();
  }, [contactId, open, initialValues]);

  const set = (field) => (event) => {
    const valor = event.target.value;
    setContact((prev) => ({ ...prev, [field]: valor }));
  };

  const name = contact.name.trim();
  const number = contact.number.trim();
  const email = contact.email.trim();

  const errors = {
    name:
      name.length < 2
        ? i18n.t("contactListItems.dialog.nameRequired")
        : name.length > 50
        ? i18n.t("contactListItems.dialog.nameTooLong")
        : "",
    number:
      number && (number.length < 8 || number.length > 50)
        ? i18n.t("contactListItems.dialog.numberInvalid")
        : "",
    email: email && !EMAIL_RE.test(email)
      ? i18n.t("contactListItems.dialog.emailInvalid")
      : "",
  };
  const hasError = Boolean(errors.name || errors.number || errors.email);

  const handleClose = () => {
    onClose();
    setContact(EMPTY);
    setTouched(false);
  };

  const handleSave = async () => {
    setTouched(true);
    if (hasError) return;
    setSaving(true);
    const values = { name, number, email };
    try {
      if (contactId) {
        await api.put(`/contact-list-items/${contactId}`, {
          ...values,
          companyId,
          contactListId,
        });
      } else {
        const { data } = await api.post("/contact-list-items", {
          ...values,
          companyId,
          contactListId,
        });
        if (onSave) {
          onSave(data);
        }
      }
      toast.success(i18n.t("contactModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  const field = (id, labelKey, value, onChange, error, extra = {}) => (
    <div>
      <label
        className={`${m.fieldLabel} ${!error && value ? m.fieldLabelFocus : ""}`}
        htmlFor={id}
      >
        {i18n.t(labelKey)}
      </label>
      <TextField
        id={id}
        variant="outlined"
        className={m.input}
        value={value}
        onChange={onChange}
        onBlur={() => setTouched(true)}
        onKeyDown={(e) => e.key === "Enter" && handleSave()}
        error={Boolean(error)}
        helperText={error || ""}
        {...extra}
      />
    </div>
  );

  const shown = (key) => (touched ? errors[key] : "");

  return (
    <AerosDialog
      open={open}
      onClose={handleClose}
      width={480}
      title={i18n.t(
        contactId
          ? "contactListItems.modal.editTitle"
          : "contactListItems.modal.addTitle"
      )}
      subtitle={i18n.t("contactListItems.modal.subtitle")}
      onSubmit={handleSave}
      loading={saving}
      submitLabel={i18n.t(
        contactId
          ? "contactListItems.dialog.okEdit"
          : "contactListItems.dialog.okAdd"
      )}
      cancelLabel={i18n.t("contactListItems.dialog.cancel")}
    >
      {field(
        "contact-item-name",
        "contactListItems.dialog.name",
        contact.name,
        set("name"),
        shown("name"),
        { autoFocus: true }
      )}
      <div className={m.grid2}>
        {field(
          "contact-item-number",
          "contactListItems.dialog.number",
          contact.number,
          set("number"),
          shown("number")
        )}
        {field(
          "contact-item-email",
          "contactListItems.dialog.email",
          contact.email,
          set("email"),
          shown("email"),
          { type: "email" }
        )}
      </div>
    </AerosDialog>
  );
};

export default ContactListItemModal;
